import { inject, Injectable, InputSignal } from '@angular/core';
import { IContent } from 'matrix-js-sdk';
import { RoomMember } from '../../../model/room-member';
import { UserService } from '../../user/user.service';
import { RoomListAccessService } from '../../room-list/room-list-access.service';
import { firstValueFrom } from 'rxjs';
import { AccountService } from '../../account/account.service';
import {
  createCustomMention,
  MentionRendererFactory
} from '../../../components/sb-client/tiptap-editor/custom-nodes/custom-mention-node/custom-mention';

@Injectable({
  providedIn: 'root'
})
/**
 * A_25437 - Erwähnungen im Chat
 */
export class MentionsService {
  public userService: UserService = inject(UserService);
  private readonly accountService = inject(AccountService);
  private readonly roomListAccessService = inject(RoomListAccessService);

  /** @deprecated Gehört, wenn überhaupt, in Helper (als static method) */
  public getMentionsFromContent(content: IContent): string[] {
    return content?.['m.mentions']?.user_ids ?? [];
  }

  public async getFilteredMentions(
    searchTerm: string,
    currentUserId: string,
    members: RoomMember[]
  ): Promise<UserMention[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    const mentionsList =
      members.map(user => ({
        id: user.mxid,
        displayname: user.name,
        avatar: this.userService.userIconCacheData.get(user.mxid) || 'assets/svgs/empty-avatar.svg',
        link: `https://matrix.to/#/${user.mxid}`
      })) || [];
    // add room
    if (members.length >= 2) {
      const room = await firstValueFrom(this.roomListAccessService.getCurrentRoomInfo$());
      if (!room) {
        throw new Error('Kein current Room für die Raummention gefunden');
      }
      mentionsList.push({
        id: 'room',
        displayname: 'alle',
        avatar: this.userService.userIconCacheData.get(room.roomId) || 'assets/svgs/empty-avatar.svg',
        link: `https://matrix.to/#/${room.roomId}`
      });
    }

    return mentionsList.filter(user => {
      const search = searchTerm.toLowerCase();
      return (
        user.id !== currentUserId &&
        (user.displayname.toLowerCase().includes(search) || user.id?.toLowerCase().includes(search))
      );
    });
  }

  /**
   * Quelle für Mention-Vorschläge.
   *
   * @param roomId
   * @param searchTerm Der Suchbegriff für die Vorschläge.
   * @param currentUserId
   */
  async mentionSourceHandler(roomId: string, searchTerm: string): Promise<UserMention[]> {
    const members = this.roomListAccessService.getModelRoom(roomId)?.members ?? [];
    return await this.getFilteredMentions(searchTerm, this.accountService.getMyMxId(), members);
  }

  createMentionExtension(roomId: InputSignal<string>, rendererFactory: MentionRendererFactory) {
    return createCustomMention(this, roomId, rendererFactory);
  }
}

export interface UserMention {
  id: string;
  displayname: string;
  avatar: string;
  link: string;
}
